import type { EventsResponse } from '@wyn/contracts';
import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { ReportButton } from '../components/Report.tsx';
import { PageTitle } from '../components/ui.tsx';
import { UI } from '../icons/ui-icons.ts';
import { eventsQuery } from '../lib/queries.ts';
import { rt } from '../lib/runtime.ts';
import { useMe } from '../lib/useMe.ts';
import { FightLog } from './Fight.tsx';
import { Loading } from './Landing.tsx';

const when = (at: number) =>
  new Date(at).toLocaleString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/** The other side of a fight report. */
const rival = (r: { attacker: string; defender: string }, me: string) => (r.attacker === me ? r.defender : r.attacker);

/** The player's event log, newest first. Rows expire after 90 days. */
export function Chronicle() {
  const me = useMe();
  const first = useQuery(eventsQuery());
  const [older, setOlder] = useState<EventsResponse['events']>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const next = cursor === undefined ? (first.data?.next ?? null) : cursor;
  const events = [...(first.data?.events ?? []), ...older];
  const name = me.data.player.name;

  const loadMore = async () => {
    if (!next) return;
    try {
      const page = await rt().api.events({ before: next });
      setOlder((o) => [...o, ...page.events]);
      setCursor(page.next);
    } catch (e) {
      setError(e);
    }
  };

  return (
    <GameShell me={me.data} skew={me.skew}>
      <section className="panel space-y-3">
        <PageTitle icon={UI.chronicle}>Chronicle</PageTitle>
        {events.length === 0 ? (
          <p className="text-ink-faint">{first.isPending ? 'Unrolling the scroll…' : 'Nothing has happened to you yet.'}</p>
        ) : (
          <ol className="space-y-1">
            {events.map((e) => (
              <li key={e.id} className="grid grid-cols-[7.5rem_1fr] gap-3 border-b border-rule/60 pb-1 last:border-0">
                <time className="font-mono text-xs leading-6 text-ink-faint" dateTime={new Date(e.at).toISOString()}>{when(e.at)}</time>
                {e.report ? (
                  <div className="flex items-start justify-between gap-2">
                    <details className="min-w-0 flex-1">
                      <summary className="cursor-pointer">{e.text}</summary>
                      <FightLog turns={e.report.turns} attacker={e.report.attacker} defender={e.report.defender} />
                    </details>
                    <ReportButton target={{ kind: 'player', name: rival(e.report, name) }} what={rival(e.report, name)} className="text-sm leading-6" />
                  </div>
                ) : (
                  <span>{e.text}</span>
                )}
              </li>
            ))}
          </ol>
        )}
        {next && <button type="button" className="btn-quiet" onClick={() => void loadMore()}>Older entries</button>}
        <ErrorNotice error={error ?? first.error} />
      </section>
    </GameShell>
  );
}
